import React, { useCallback, useEffect, useRef, useState } from 'react';
import { nanoid } from 'nanoid';
import { useLocation, useParams } from 'react-router';
import { AppInteractionType, PermissionContext, } from '@tribeplatform/gql-client/types';
import { useQuery } from '@tribeplatform/react-components/common';
import { useTribeClient } from '@tribeplatform/react-sdk';
import { useAuthMember } from '@tribeplatform/react-sdk/hooks';
import { SlateRenderer } from '../../components/index.js';
import { useSlate } from '../../hooks/index.js';
import { LoadingComponent } from './LoadingComponent.js';
export const DynamicBlock = ({ appId, blockKey, context: customContext, entityId: customEntityId, staffOnly, }) => {
    var _a;
    const interactionId = useRef(nanoid());
    const queryParams = useQuery();
    const urlParams = useParams();
    const { pathname } = useLocation();
    const { mode, context: slateContext, extraContext: parentExtraContext, } = useSlate();
    const [slate, setSlate] = useState(undefined);
    const { data: member } = useAuthMember({ fields: 'basic' });
    const hidden = staffOnly && !(member === null || member === void 0 ? void 0 : member.teammate);
    const { config } = useTribeClient();
    const { interactionsContext: { useInteractions, interactionEmitter }, } = config !== null && config !== void 0 ? config : {};
    const { loadInteractions } = useInteractions({
        dynamicBlockKey: blockKey,
    });
    const getContextAndEntity = useCallback(() => {
        if (slateContext && !customContext) {
            if (slateContext === null || slateContext === void 0 ? void 0 : slateContext.memberId) {
                return {
                    context: PermissionContext.MEMBER,
                    entityId: slateContext.memberId,
                };
            }
            if (slateContext === null || slateContext === void 0 ? void 0 : slateContext.postTypeId) {
                return {
                    context: PermissionContext.POST,
                    entityId: slateContext.postTypeId,
                };
            }
            if (slateContext === null || slateContext === void 0 ? void 0 : slateContext.postId) {
                return {
                    context: PermissionContext.POST,
                    entityId: slateContext.postId,
                };
            }
            if (slateContext === null || slateContext === void 0 ? void 0 : slateContext.spaceId) {
                return {
                    context: PermissionContext.SPACE,
                    entityId: slateContext.spaceId,
                };
            }
        }
        return {
            context: customContext !== null && customContext !== void 0 ? customContext : PermissionContext.NETWORK,
            entityId: customEntityId,
        };
    }, [slateContext, customEntityId, customContext]);
    const { context, entityId } = (_a = getContextAndEntity()) !== null && _a !== void 0 ? _a : {};
    const extraContext = Object.assign(Object.assign({}, parentExtraContext), { slate: Object.assign(Object.assign({}, parentExtraContext.slate), { callback: async (id, options = {}) => {
                const { data } = options;
                await callLoadInteractions({
                    callbackId: id,
                    inputs: data,
                    onError: error => {
                        throw error;
                    },
                });
            } }) });
    const callLoadInteractions = useCallback(async (options = {}) => {
        const { callbackId, inputs, onError } = options;
        try {
            await loadInteractions({
                interactionId: interactionId.current,
                appId,
                context,
                entityId,
                preview: mode !== 'live',
                dynamicBlockKey: blockKey,
                callbackId,
                inputs,
            });
        }
        catch (error) {
            if (onError) {
                onError(error);
            }
        }
    }, [appId, entityId, context, blockKey, loadInteractions, mode]);
    useEffect(() => {
        if (!hidden) {
            callLoadInteractions();
        }
    }, [hidden, appId, urlParams === null || urlParams === void 0 ? void 0 : urlParams.appSlug, callLoadInteractions]);
    useEffect(() => {
        const showListener = (args) => {
            if (args.interactionId === interactionId.current) {
                if (args.slate) {
                    setSlate(args.slate);
                }
            }
        };
        const reloadListener = (args) => {
            var _a, _b;
            if (args.interactionId === interactionId.current ||
                ((_b = (_a = args.props) === null || _a === void 0 ? void 0 : _a.dynamicBlockKeys) === null || _b === void 0 ? void 0 : _b.includes(blockKey))) {
                callLoadInteractions();
            }
        };
        if (interactionEmitter) {
            if (interactionId || blockKey) {
                interactionEmitter.on(AppInteractionType.Show, showListener);
                interactionEmitter.on(AppInteractionType.Reload, reloadListener);
            }
        }
        return () => {
            if (interactionEmitter) {
                if (interactionId || blockKey) {
                    interactionEmitter.removeListener(AppInteractionType.Show, showListener);
                    interactionEmitter.removeListener(AppInteractionType.Reload, reloadListener);
                }
            }
        };
    }, [
        interactionId,
        blockKey,
        loadInteractions,
        callLoadInteractions,
        interactionEmitter,
    ]);
    if (hidden) {
        return null;
    }
    if (!slate) {
        return React.createElement(LoadingComponent, null);
    }
    return (React.createElement(SlateRenderer, { slate: slate, context: Object.assign(Object.assign({ path: pathname, urlParams,
            queryParams }, slateContext), { appId, dynamicBlockKey: blockKey }), extraContext: extraContext }));
};
//# sourceMappingURL=Block.js.map